/**
 * @module AdapterCard
 * @package @geenius/adapters/solidjs-css
 * @description Renders the vanilla CSS adapter summary card for the SolidJS
 * CSS variant. The card surfaces provider status, latency, and domain metadata
 * for one adapter domain in the dashboard views.
 */

import type { AdapterDomain, AdapterStatusInfo } from "@geenius/adapters";
import {
  DOMAIN_DESCRIPTIONS,
  DOMAIN_ICONS,
  DOMAIN_LABELS,
} from "@geenius/adapters";
import type { JSX } from "solid-js";
import { Show } from "solid-js";
import { cx } from "../lib/cx";
import { AdapterStatusBadge } from "./AdapterStatusBadge";

/**
 * Props for rendering a single adapter summary card in the SolidJS CSS dashboard grid.
 *
 * @property domain Adapter domain represented by the card.
 * @property status Current provider health and metadata for the domain.
 * @property onClick Optional callback fired when the card is activated.
 * @property class Optional class name merged into the card container.
 */
export interface AdapterCardProps {
  domain: AdapterDomain;
  status: AdapterStatusInfo;
  onClick?: () => void;
  class?: string;
}

/**
 * Renders a clickable SolidJS CSS summary card for one adapter domain.
 *
 * @param props Card props including the adapter domain, status payload, and click handler.
 * @returns A button element styled by the vanilla CSS variant.
 */
export function AdapterCard(props: AdapterCardProps): JSX.Element {
  const ariaLabel = () =>
    `${DOMAIN_LABELS[props.domain]} adapter - ${props.status.status}`;

  const body = (
    <>
      <div aria-hidden="true" class="gn-adapters-card__icon">
        {DOMAIN_ICONS[props.domain]}
      </div>
      <div class="gn-adapters-card__body">
        <div class="gn-adapters-card__header">
          <h3 class="gn-adapters-card__name">{DOMAIN_LABELS[props.domain]}</h3>
          <AdapterStatusBadge status={props.status.status} />
        </div>
        <p class="gn-adapters-card__desc">
          {DOMAIN_DESCRIPTIONS[props.domain]}
        </p>
        <Show when={props.status.provider && props.status.provider !== "none"}>
          <div class="gn-adapters-card__meta">
            <span class="gn-adapters-card__provider">
              {props.status.provider}
            </span>
            <Show when={props.status.latency != null}>
              <span class="gn-adapters-card__latency">
                {props.status.latency}ms
              </span>
            </Show>
          </div>
        </Show>
        <Show when={props.status.error}>
          <p class="gn-adapters-card__error">{props.status.error}</p>
        </Show>
      </div>
    </>
  );

  // Static branch (no click handler): render an <article>, not a focusable
  // no-op button. Matches the SolidJS and React-CSS reference behavior and
  // UI_PROTOCOL §7 (don't expose decorative elements as buttons).
  if (!props.onClick) {
    return (
      <article
        aria-label={ariaLabel()}
        class={cx("gn-adapters-card", props.class)}
      >
        {body}
      </article>
    );
  }

  return (
    <button
      type="button"
      onClick={() => props.onClick?.()}
      class={cx("gn-adapters-card", props.class)}
      aria-label={ariaLabel()}
    >
      {body}
    </button>
  );
}
